import { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAngleLeft,
  faAngleRight,
  faDownload,
  faFileWord,
  faPrint,
  faSpinner,
} from '@fortawesome/free-solid-svg-icons';
import Button from 'components/inputs/Button';
import {
  useExportExportManagementReportHook,
  useExportExportManagementReportWordHook,
} from 'api/export/export';
import { downloadBlob } from 'utils/file-utils';
import { formatDate } from 'utils/date-helpers';
import { showException } from 'utils/exception-helper';
import useMe from 'contexts/authentication/useMe';
import {
  BookedToChangesPanel,
  CancellationsPanel,
  DeliveredCarsPanel,
  GapsPanel,
  HeadlineTrend,
  hrs,
  LeadTimePanel,
  MEASURES,
  MethodPanel,
  nf,
  pct,
  SatisfactionPanel,
  TransportMixPanel,
  TrendPanel,
  TruckFillRatePanel,
  UtilizationPanel,
  YearOverviewPanel,
} from './panels';
import {
  Finding,
  Grid,
  Headline,
  Headlines,
  Kicker,
  Masthead,
  MetaRow,
  RangePick,
  ReportFooter,
  ReportGlobalStyle,
  ReportRoot,
  Segmented,
  Sheet,
  StateBox,
  Title,
  Toolbar,
} from './reportStyles';
import DatePicker, { DateRangeInput } from 'components/inputs/DatePicker';
import useReportData, {
  MAX_WEEKS_BACK,
  monthsOf,
  periodOf,
  PeriodSelection,
  SHORTCUTS,
} from './useReportData';
import { HeadlineSpark, MonthlyChartPanel, MonthlyPanel, monthlySeries } from './monthly';

/**
 * Ledningsrapporten som en egen sida.
 *
 * Sidan har två uppgifter och det är därför den ser ut som den gör. På skärmen är den en
 * översikt; i webbläsarens utskrift är den rapporten som mailas till RM:s ledningsgrupp.
 * Utskriften sker i läsarens egen webbläsare — den har redan en PDF-motor, så vi behöver
 * ingen på servern och slipper både ett licensbelastat beroende och en Chromium-binär i
 * App Service.
 *
 * Widgetarna i RM-nyckeltal finns kvar orörda. Den vyn är till för att gräva i talen, den här
 * för att läsa dem.
 */
const ManagementReportPage = () => {
  const [selection, setSelection] = useState<PeriodSelection>(SHORTCUTS[0].selection);
  const period = useMemo(() => periodOf(selection), [selection]);

  /**
   * Det halvvalda spannet, medan användaren klickar.
   *
   * Väljaren är kontrollerad, så den måste få se sitt eget första klick. Läses start och slut
   * bara ur den valda perioden står startdatumet stilla mellan klicken, och väljaren tolkar det
   * andra klicket som ett nytt första — spannet går då aldrig att slutföra.
   */
  const [draftRange, setDraftRange] = useState<[Date | null, Date | null] | null>(null);

  const selectPeriod = (next: PeriodSelection) => {
    setDraftRange(null);
    setSelection(next);
  };

  // Stegningen finns bara för veckor. Ett fritt spann har ingen självklar nästa — och året till
  // dags datum har ingen alls, det slutar på dagens datum.
  const weeksBack = selection.kind === 'week' ? selection.weeksBack : null;
  const stepWeeks = (delta: number) => {
    if (weeksBack === null) return;
    const next = weeksBack + delta;
    if (next < 0 || next > MAX_WEEKS_BACK) return;
    selectPeriod({ kind: 'week', weeksBack: next });
  };

  // Samma gräns som veckostegarens tak, av samma skäl: bortom underlaget blir varje panel tom
  // utan att säga varför. Utan gränser här kunde spannväljaren gå dit stegaren är spärrad från
  // — ett år i framtiden ger en rapport full av nollor och en jämförelseperiod som också ligger
  // utanför underlaget, utan att något förklarar det.
  const [earliestSelectable, latestSelectable] = useMemo(() => {
    const earliest = periodOf({ kind: 'week', weeksBack: MAX_WEEKS_BACK }).start;
    return [earliest, new Date()];
  }, []);

  const { data, previous, monthlyLeadTime, weeks, isLoading, error, filter } =
    useReportData(period);

  /**
   * Årsläget visar också talen per månad, om användaren inte väljer bort det. Valet finns för
   * utskriften: den som vill skicka en sida med bara årets summor ska kunna få det.
   */
  const [showMonthly, setShowMonthly] = useState(true);

  // Månadsuppdelningen behöver minst två månader för att säga något. I januari är den en kopia
  // av totalen med en extra rubrik.
  const series = useMemo(() => {
    if (!period.monthly || !showMonthly || !data || !monthlyLeadTime) return null;
    const months = monthsOf(period.start, period.end);
    if (months.length < 2) return null;
    return monthlySeries(
      months,
      monthsOf(period.comparison.start, period.comparison.end),
      data,
      previous,
      monthlyLeadTime
    );
  }, [period, showMonthly, data, previous, monthlyLeadTime]);
  const me = useMe();

  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadingWord, setIsDownloadingWord] = useState(false);
  const downloadExcel = useExportExportManagementReportHook();
  const downloadWord = useExportExportManagementReportWordHook();

  // Ett namn för alla tre uttagen. De tre filerna är samma rapport i tre former och hamnar i
  // samma nedladdningsmapp; heter de olika går de inte att sortera ihop, och den som fått dem
  // mailade kan inte se att de hör ihop. Bara ändelsen skiljer.
  const fileBaseName = `Zakra_Ledningsrapport_${formatDate(period.start)}_${formatDate(period.end)}`;

  /**
   * Skriver ut sidan med rapportens namn i stället för flikens.
   *
   * Webbläsarens "Spara som PDF" döper filen efter `document.title`, som här är "Zäkra Web" —
   * så PDF:en landade som Zäkra Web.pdf medan Excel-filen fick ett namn med period i. Titeln
   * byts därför under utskriften och återställs efteråt. Återställningen hänger på
   * `afterprint` eftersom `window.print()` inte är pålitligt blockerande i alla webbläsare;
   * händelsen kommer både när utskriften görs och när dialogen avbryts.
   */
  const printReport = () => {
    const previousTitle = document.title;
    const restore = () => {
      document.title = previousTitle;
      window.removeEventListener('afterprint', restore);
    };
    window.addEventListener('afterprint', restore);
    document.title = fileBaseName;
    window.print();
  };

  const lead = data?.leadTime;
  // Andelen körning ställs mot medianen — samma två tal som står i meningen. Körtiden är ett
  // medelvärde per bil och kan i en skev period hamna i nivå med eller över medianledtiden; då
  // finns ingen väntetid att uttala sig om, och iakttagelsen säger inget om den.
  const driveShare =
    lead && lead.medianHours > 0 && lead.activeHours < lead.medianHours
      ? (lead.activeHours / lead.medianHours) * 100
      : null;

  return (
    <ReportRoot>
      <ReportGlobalStyle />
      <Sheet>
        <Masthead>
          <Kicker>ZäkraGo · Riddermark · ledningsrapport</Kicker>
          <Title>
            {formatDate(period.start)} – {formatDate(period.end)}
          </Title>
          <MetaRow>
            <span>
              <b>Period</b> {period.label.toLowerCase()}
            </span>
            <span>
              <b>Uttagen</b> {formatDate(new Date())}
            </span>
            {me?.name ? (
              <span>
                <b>Av</b> {me.name}
              </span>
            ) : null}
            <span>
              <b>Underlag</b> ZäkraGo, ärenden stängda i perioden
            </span>
            <span>
              <b>Jämförs mot</b> {formatDate(period.comparison.start)} –{' '}
              {formatDate(period.comparison.end)}
            </span>
            {period.weeks ? (
              <span>
                <b>Veckor</b> {formatDate(period.weeks.current.start)} –{' '}
                {formatDate(period.weeks.current.end)} och{' '}
                {formatDate(period.weeks.previous.start)} –{' '}
                {formatDate(period.weeks.previous.end)}
              </span>
            ) : null}
          </MetaRow>
        </Masthead>

        <Toolbar>
          <Segmented role="group" aria-label="Period">
            {SHORTCUTS.map((shortcut) => (
              <button
                key={shortcut.label}
                type="button"
                aria-pressed={
                  shortcut.selection.kind === 'week'
                    ? weeksBack === shortcut.selection.weeksBack
                    : selection.kind === shortcut.selection.kind
                }
                onClick={() => selectPeriod(shortcut.selection)}
              >
                {shortcut.label}
              </button>
            ))}
          </Segmented>

          {period.monthly ? (
            <Segmented role="group" aria-label="Visa året">
              <button
                type="button"
                aria-pressed={!showMonthly}
                onClick={() => setShowMonthly(false)}
              >
                Totalt
              </button>
              <button
                type="button"
                aria-pressed={showMonthly}
                onClick={() => setShowMonthly(true)}
              >
                Per månad
              </button>
            </Segmented>
          ) : null}

          {/*
            Stega mellan rapportveckor. Snabbvalen räcker till de två senaste; den som ska titta
            på en vecka längre bak skulle annars behöva räkna ut fredagsdatumen för hand i
            datumväljaren — och en vecka som inte börjar på en fredag är inte samma mått.
            Knapparna är avstängda utanför veckoläget, eftersom "veckan innan" inte betyder något
            för året till dags datum.
          */}
          <Segmented role="group" aria-label="Byt rapportvecka">
            <button
              type="button"
              disabled={weeksBack === null || weeksBack >= MAX_WEEKS_BACK}
              title="Föregående rapportvecka"
              onClick={() => stepWeeks(1)}
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </button>
            <button
              type="button"
              disabled={weeksBack === null || weeksBack === 0}
              title="Nästa rapportvecka"
              onClick={() => stepWeeks(-1)}
            >
              <FontAwesomeIcon icon={faAngleRight} />
            </button>
          </Segmented>

          <RangePick>
            <DatePicker
              calendarStartDay={1}
              customInput={
                <DateRangeInput
                  daterange={draftRange ?? [period.start, period.end]}
                />
              }
              selectsRange
              startDate={draftRange ? draftRange[0] : period.start}
              endDate={draftRange ? draftRange[1] : period.end}
              dateFormat="yyyy-MM-dd"
              locale="sv"
              onChange={(range: [Date | null, Date | null]) => {
                const [start, end] = range;
                // Väljaren rapporterar varje klick, så det första kommer med end = null. Behåll
                // det som halvvalt och byt period först när båda ändarna finns — annars laddas
                // rapporten om för en halvvald period och blinkar till mellan klicken.
                if (!start || !end) {
                  setDraftRange(range);
                  return;
                }
                // Dygnen tas med i sin helhet: startdagen från midnatt, slutdagen till sista
                // sekunden. Väljaren ger midnatt i båda ändar, och utan det här saknas hela
                // slutdagen i rapporten.
                const from = new Date(start);
                from.setHours(0, 0, 0, 0);
                const to = new Date(end);
                to.setHours(23, 59, 59, 999);
                setDraftRange(null);
                setSelection({ kind: 'custom', start: from, end: to });
              }}
              minDate={earliestSelectable}
              maxDate={latestSelectable}
              // Stängs kalendern med bara ena änden vald måste det halvvalda spannet släppas.
              // Annars står fältet kvar med ett startdatum och en tom högerhalva medan rapporten
              // under visar en helt annan period, och det går inte att ta sig ur utan att välja
              // om från början.
              onCalendarClose={() => setDraftRange(null)}
              populateHolidays
              shouldCloseOnSelect
              showYearDropdown
              showMonthDropdown
              showWeekNumbers
            />
          </RangePick>

          <Button onClick={printReport}>
            <FontAwesomeIcon icon={faPrint} /> Skriv ut / spara som PDF
          </Button>

          <Button
            disabled={isDownloading}
            onClick={async () => {
              try {
                setIsDownloading(true);
                const result = await downloadExcel(filter);
                if (result) {
                  downloadBlob(result, `${fileBaseName}.xlsx`);
                }
              } catch (e) {
                showException(e);
              } finally {
                setIsDownloading(false);
              }
            }}
          >
            <FontAwesomeIcon icon={faDownload} /> Underlaget som Excel
            {isDownloading ? (
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginLeft: '5px' }} />
            ) : null}
          </Button>

          {/*
            Word-filen är den här sidan som ett dokument, inte underlaget. Utskriften ovan ger
            en PDF som ingen kan skriva i; ledningsgruppen vill kunna lägga egna kommentarer
            bredvid talen innan rapporten skickas vidare, och då måste filen vara redigerbar.
            Jämförelseperioden skickas med som datum — backend kopierar resten av filtret, så
            trendkolumnerna i dokumentet räknas på exakt samma urval som sidan visar.
          */}
          <Button
            disabled={isDownloadingWord}
            onClick={async () => {
              try {
                setIsDownloadingWord(true);
                const result = await downloadWord({
                  filter,
                  comparisonStartDate: period.comparison.start,
                  comparisonEndDate: period.comparison.end,
                  periodLabel: period.label,
                  comparisonLabel: period.comparison.label,
                });
                if (result) {
                  downloadBlob(result, `${fileBaseName}.docx`);
                }
              } catch (e) {
                showException(e);
              } finally {
                setIsDownloadingWord(false);
              }
            }}
          >
            <FontAwesomeIcon icon={faFileWord} /> Rapporten som Word
            {isDownloadingWord ? (
              <FontAwesomeIcon icon={faSpinner} spin style={{ marginLeft: '5px' }} />
            ) : null}
          </Button>
        </Toolbar>

        {isLoading ? (
          <StateBox>
            <FontAwesomeIcon icon={faSpinner} spin /> Hämtar rapporten …
          </StateBox>
        ) : null}

        {error && !isLoading ? (
          <StateBox>Rapporten kunde inte hämtas. Ladda om sidan och försök igen.</StateBox>
        ) : null}

        {data && !isLoading ? (
          <>
            <Headlines>
              <Headline>
                <div className="hl-label">Levererade bilar</div>
                <div className="hl-value">{nf(data.deliveredCars.totalDeliveredCars)}</div>
                <div className="hl-sub">
                  {nf(data.deliveredCars.openCasesOlderThanPeriod)} öppna ärenden äldre än perioden
                </div>
                <HeadlineTrend
                  measureKey="delivered"
                  data={data}
                  previous={previous}
                  comparisonLabel={period.comparison.label}
                />
                <HeadlineSpark measureKey="delivered" series={series} />
              </Headline>
              <Headline>
                <div className="hl-label">Bilar på transport · median</div>
                {/* Inga mätbara bilar ger 0 från backend, och "0,0 h" på papper läses som en
                    sanning om veckan. Brickan visar streck, som kundnöjdheten gör. */}
                <div className="hl-value">
                  {data.leadTime.cars > 0 ? (
                    <>
                      {nf(data.leadTime.medianHours, 1)}
                      <small> h</small>
                    </>
                  ) : (
                    '–'
                  )}
                </div>
                <div className="hl-sub">
                  {data.leadTime.cars > 0
                    ? `${nf(data.leadTime.cars)} bilar · ${nf(data.leadTime.activeHours, 1)} h körning per bil`
                    : 'inga mätbara bilar i perioden'}
                </div>
                <HeadlineTrend
                  measureKey="leadTime"
                  data={data}
                  previous={previous}
                  comparisonLabel={period.comparison.label}
                />
                <HeadlineSpark measureKey="leadTime" series={series} />
              </Headline>
              <Headline>
                <div className="hl-label">Fyllnadsgrad lastbil</div>
                <div className="hl-value">
                  {nf(data.truckCoverage.totalFillRatePercentage ?? 0, 1)}
                  <small> %</small>
                </div>
                <div className="hl-sub">
                  in {pct(data.truckCoverage.inboundFillRatePercentage)} · ut{' '}
                  {pct(data.truckCoverage.outboundFillRatePercentage)}
                </div>
                <HeadlineTrend
                  measureKey="truckFill"
                  data={data}
                  previous={previous}
                  comparisonLabel={period.comparison.label}
                />
                <HeadlineSpark measureKey="truckFill" series={series} />
              </Headline>
              <Headline>
                <div className="hl-label">Kundnöjdhet</div>
                <div className="hl-value">
                  {data.satisfaction.averageRating !== undefined &&
                  data.satisfaction.averageRating !== null
                    ? nf(data.satisfaction.averageRating, 2)
                    : '–'}
                </div>
                <div className="hl-sub">
                  av 5 · {nf(data.satisfaction.totalResponses)} svar
                </div>
                <HeadlineTrend
                  measureKey="satisfaction"
                  data={data}
                  previous={previous}
                  comparisonLabel={period.comparison.label}
                />
                <HeadlineSpark measureKey="satisfaction" series={series} />
              </Headline>
            </Headlines>

            {lead && lead.cars > 0 ? (
              <Finding>
                <div className="f-title">Periodens iakttagelse</div>
                <p>
                  En inköps- eller inbytesbil är på väg{' '}
                  <strong>{hrs(lead.medianHours)}</strong> — medianen, alltså den mittersta bilen
                  i perioden.{' '}
                  {driveShare !== null ? (
                    <>
                      Av den tiden är <strong>{nf(lead.activeHours, 1)} timmar</strong> faktisk
                      körning, {pct(driveShare)}. Resten står bilen still, vilket gör ledtiden
                      till ett planeringsproblem snarare än ett avståndsproblem.
                    </>
                  ) : (
                    <>
                      Körtiden ligger på <strong>{nf(lead.activeHours, 1)} timmar</strong> per
                      bil, i nivå med ledtiden: den här perioden går tiden till körning och inte
                      till väntan.
                    </>
                  )}
                </p>
              </Finding>
            ) : null}

            <Grid>
              {/* Årsrapportens tabell har fjolårets kolumner i sig, så den ersätter trendtabellen
                  i stället för att stå bredvid en kopia av halva sig själv. */}
              {period.weeks && weeks ? (
                <YearOverviewPanel
                  data={data}
                  previous={previous}
                  weeks={weeks}
                  periodStart={period.start}
                  periodEnd={period.end}
                  comparisonLabel={period.comparison.label}
                />
              ) : (
                <TrendPanel
                  data={data}
                  previous={previous}
                  comparisonLabel={period.comparison.label}
                />
              )}
            </Grid>

            {series ? (
              <>
                <Grid>
                  <MonthlyPanel
                    series={series}
                    data={data}
                    comparisonLabel={period.comparison.label}
                  />
                </Grid>
                <Grid>
                  <MonthlyChartPanel series={series} measureKey="delivered" />
                </Grid>
                {/* Samma ordning som tabellen ovan, två i bredd. Levererade bilar står ensam för att den är
                    rapportens huvudtal och har störst spann mellan månaderna. */}
                <Grid className="zg-pagebreak">
                  {MEASURES.filter((m) => m.key !== 'delivered').map((m) => (
                    <MonthlyChartPanel key={m.key} series={series} measureKey={m.key} span={6} />
                  ))}
                </Grid>
              </>
            ) : null}

            <Grid>
              <DeliveredCarsPanel data={data} previous={previous} />
              <TransportMixPanel data={data} previous={previous} />
            </Grid>

            <Grid className="zg-pagebreak">
              <LeadTimePanel data={data} />
            </Grid>

            <Grid>
              <CancellationsPanel data={data} previous={previous} />
              <SatisfactionPanel data={data} previous={previous} />
            </Grid>

            <Grid>
              <BookedToChangesPanel data={data} previous={previous} />
            </Grid>

            <Grid className="zg-pagebreak">
              <UtilizationPanel data={data} />
              <TruckFillRatePanel data={data} previous={previous} />
            </Grid>

            <Grid className="zg-pagebreak">
              <GapsPanel data={data} />
              <MethodPanel />
            </Grid>

            <ReportFooter>
              <span>
                Ledningsrapport · ZäkraGo · sammanfattning, fullständigt underlag i Excel-filen
              </span>
            </ReportFooter>
          </>
        ) : null}
      </Sheet>
    </ReportRoot>
  );
};

export default ManagementReportPage;
